import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  Filter,
  CalendarDays,
  CheckCircle2,
  XCircle,
  Save,
  Play,
  Loader2,
  ExternalLink,
} from "lucide-react";

import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { listAllSimulationEvents } from "@/lib/api/simulations";
import { listSimulators } from "@/lib/api/simulators";
import { listUsers } from "@/lib/api/users";
import { ViewerGuard } from "@/lib/auth/AdminGuard";
import type { SimulationEventAdmin } from "@/types";

export const Route = createFileRoute("/_dashboard/admin/simulation-events")({
  component: AdminSimulationEventsPage,
});

function formatDateParis(dateStr: string): string {
  let d: Date;
  if (!dateStr.endsWith("Z") && !dateStr.includes("+")) {
    d = new Date(dateStr + "Z");
  } else {
    d = new Date(dateStr);
  }
  return new Intl.DateTimeFormat("fr-FR", {
    timeZone: "Europe/Paris",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(d);
}

function AdminSimulationEventsPage() {
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [simulatorFilter, setSimulatorFilter] = useState<string>("all");
  const [userFilter, setUserFilter] = useState<string>("all");
  const [successFilter, setSuccessFilter] = useState<string>("all");
  const [saveFilter, setSaveFilter] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");

  const { data: simulatorsData } = useQuery({
    queryKey: ["simulators-list"],
    queryFn: () =>
      listSimulators({ page_size: 100, include_unpublished: true }),
  });

  const { data: usersData } = useQuery({
    queryKey: ["users-list"],
    queryFn: () => listUsers({ pageSize: 100 }),
  });

  const { data, isLoading } = useQuery({
    queryKey: [
      "admin-simulation-events",
      page,
      pageSize,
      simulatorFilter,
      userFilter,
      successFilter,
      saveFilter,
      dateFrom,
      dateTo,
    ],
    queryFn: () =>
      listAllSimulationEvents({
        page,
        page_size: pageSize,
        simulator_id:
          simulatorFilter !== "all"
            ? parseInt(simulatorFilter)
            : undefined,
        user_id:
          userFilter !== "all" ? parseInt(userFilter) : undefined,
        success:
          successFilter !== "all"
            ? successFilter === "true"
            : undefined,
        is_save:
          saveFilter !== "all"
            ? saveFilter === "true"
            : undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      }),
  });

  const resetFilters = () => {
    setSimulatorFilter("all");
    setUserFilter("all");
    setSuccessFilter("all");
    setSaveFilter("all");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const hasActiveFilters =
    simulatorFilter !== "all" ||
    userFilter !== "all" ||
    successFilter !== "all" ||
    saveFilter !== "all" ||
    dateFrom !== "" ||
    dateTo !== "";

  const handleOpenCotation = (event: SimulationEventAdmin) => {
    if (!event.simulation_id || !event.simulator_id) return;
    sessionStorage.setItem(
      `simulation_inputs_${event.simulation_id}`,
      JSON.stringify(event.inputs || {})
    );
    const url = `/simulators/${event.simulator_id}/simulate?version=${event.version_id}&loadSimulation=${event.simulation_id}&t=${Date.now()}`;
    window.open(url, "_blank");
  };

  return (
    <ViewerGuard>
      <div className="flex flex-col h-full">
        <Header title="Toutes les simulations" />

        <div className="flex-1 p-6 space-y-6 overflow-auto">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Filter className="h-4 w-4" />
                  <span className="text-sm font-medium">Filtres</span>
                </div>

                <Select
                  value={simulatorFilter}
                  onValueChange={(v) => {
                    setSimulatorFilter(v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Simulateur" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      Tous les simulateurs
                    </SelectItem>
                    {simulatorsData?.items.map((sim) => (
                      <SelectItem
                        key={sim.id}
                        value={String(sim.id)}
                      >
                        {sim.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={userFilter}
                  onValueChange={(v) => {
                    setUserFilter(v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Utilisateur" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      Tous les utilisateurs
                    </SelectItem>
                    {usersData?.items.map((user) => (
                      <SelectItem
                        key={user.id}
                        value={String(user.id)}
                      >
                        {user.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={successFilter}
                  onValueChange={(v) => {
                    setSuccessFilter(v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Résultat" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="true">Succès</SelectItem>
                    <SelectItem value="false">Échec</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={saveFilter}
                  onValueChange={(v) => {
                    setSaveFilter(v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les types</SelectItem>
                    <SelectItem value="false">Calcul (aperçu)</SelectItem>
                    <SelectItem value="true">Enregistrement</SelectItem>
                  </SelectContent>
                </Select>

                <div className="flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="evt-date-from" className="sr-only">
                      Du
                    </Label>
                    <Input
                      id="evt-date-from"
                      type="date"
                      value={dateFrom}
                      onChange={(e) => {
                        setDateFrom(e.target.value);
                        setPage(1);
                      }}
                      className="h-9 w-[140px] text-sm"
                    />
                    <span className="text-xs text-muted-foreground">
                      –
                    </span>
                    <Label htmlFor="evt-date-to" className="sr-only">
                      Au
                    </Label>
                    <Input
                      id="evt-date-to"
                      type="date"
                      value={dateTo}
                      onChange={(e) => {
                        setDateTo(e.target.value);
                        setPage(1);
                      }}
                      className="h-9 w-[140px] text-sm"
                    />
                  </div>
                </div>

                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetFilters}
                  >
                    Réinitialiser
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table className="min-w-[1280px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap">
                        Date / Heure
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        Simulateur
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        Utilisateur
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        Résultat
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        Type
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        Temps (ms)
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        Référence cotation
                      </TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      [...Array(5)].map((_, i) => (
                        <TableRow key={i}>
                          {[...Array(8)].map((_, j) => (
                            <TableCell key={j}>
                              <Skeleton className="h-4 w-20" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : data?.items.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={8}
                          className="h-32 text-center"
                        >
                          <div className="flex flex-col items-center gap-2 text-muted-foreground">
                            <Activity className="h-10 w-10 opacity-50" />
                            <p>Aucune simulation trouvée</p>
                            {hasActiveFilters && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={resetFilters}
                              >
                                Réinitialiser les filtres
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.items.map((event, index) => (
                        <TableRow
                          key={event.id}
                          className="border-b transition-colors hover:bg-muted/50"
                          style={{
                            animation: `fadeInUp 0.3s ease-out ${index * 0.03}s both`,
                          }}
                        >
                          <TableCell className="text-sm whitespace-nowrap">
                            {formatDateParis(event.created_at)}
                          </TableCell>

                          <TableCell className="text-sm">
                            <span className="truncate max-w-[180px] block">
                              {event.simulator_name}
                            </span>
                          </TableCell>

                          <TableCell className="text-sm">
                            <span
                              className="truncate max-w-[200px] block"
                              title={event.user_email || undefined}
                            >
                              {event.user_email || "—"}
                            </span>
                          </TableCell>

                          <TableCell>
                            {event.success ? (
                              <Badge
                                variant="success"
                                className="gap-1 whitespace-nowrap"
                              >
                                <CheckCircle2 className="h-3 w-3" />
                                Succès
                              </Badge>
                            ) : (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Badge
                                    variant="destructive"
                                    className="gap-1 whitespace-nowrap cursor-pointer hover:opacity-80"
                                  >
                                    <XCircle className="h-3 w-3" />
                                    Échec
                                  </Badge>
                                </PopoverTrigger>
                                <PopoverContent
                                  align="start"
                                  className="w-80 max-h-60 overflow-y-auto"
                                >
                                  <div className="space-y-2">
                                    <h4 className="font-medium text-sm text-destructive flex items-center gap-1.5">
                                      <XCircle className="h-4 w-4" />
                                      {event.errors.length > 1
                                        ? `${event.errors.length} erreurs`
                                        : "1 erreur"}
                                    </h4>
                                    <ul className="space-y-1.5">
                                      {event.errors.map((err, i) => (
                                        <li
                                          key={i}
                                          className="text-sm text-muted-foreground bg-muted/50 rounded px-2 py-1.5"
                                        >
                                          {err}
                                        </li>
                                      ))}
                                    </ul>
                                    {event.errors.length === 0 && (
                                      <p className="text-sm text-muted-foreground italic">
                                        Aucun détail d'erreur disponible
                                      </p>
                                    )}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            )}
                          </TableCell>

                          <TableCell>
                            {event.is_save ? (
                              <Badge
                                variant="default"
                                className="gap-1 whitespace-nowrap"
                              >
                                <Save className="h-3 w-3" />
                                Enregistrement
                              </Badge>
                            ) : (
                              <Badge
                                variant="secondary"
                                className="gap-1 whitespace-nowrap"
                              >
                                <Play className="h-3 w-3" />
                                Calcul
                              </Badge>
                            )}
                          </TableCell>

                          <TableCell className="text-sm font-mono">
                            {event.execution_time_ms} ms
                          </TableCell>

                          <TableCell className="text-sm">
                            <span
                              className="font-mono text-xs truncate max-w-[200px] block"
                              title={
                                event.simulation_ref || undefined
                              }
                            >
                              {event.simulation_ref || "—"}
                            </span>
                          </TableCell>

                          <TableCell>
                            {event.simulation_id && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() =>
                                  handleOpenCotation(event)
                                }
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Pagination */}
          {data && data.total_pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {data.total_items} simulation
                {data.total_items > 1 ? "s" : ""} au total
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setPage((p) => Math.max(1, p - 1))
                  }
                  disabled={page === 1}
                >
                  Précédent
                </Button>
                <div className="flex items-center gap-1 px-3 py-1 bg-muted rounded text-sm">
                  {page} / {data.total_pages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setPage((p) =>
                      Math.min(data.total_pages, p + 1)
                    )
                  }
                  disabled={page === data.total_pages}
                >
                  Suivant
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </ViewerGuard>
  );
}
